import {
  Component,
  Input,
  ViewChildren,
  QueryList,
  ElementRef,
} from "@angular/core";
import {
  GradingStagingData,
  GradingInput,
} from "../../../models/grading.model";
import { ValidationTypes } from "../../../models/meta-data.model";

@Component({
  selector: "lowy-etdrs-grid",
  templateUrl: "./etdrs-grid.component.html",
  styleUrls: ["./etdrs-grid.component.scss"],
  providers: [],
})
export class EtdrsComponent {
  @Input() id: string;
  @Input() disabled: boolean;
  @Input() tabIndex: string;

  @ViewChildren("gridInput") inputViewChildren!: QueryList<Input>;

  public inputs: any[];
  public inputModels: number[]; // indexed off inputs.position
  public maxlength; // max length of etdrs input
  public tooltip;

  public allowedValues = null;
  public threeDigitsDecimalMax10 = false;
  public threeDigitsDecimalMax10orTripleDigits = false;

  public _gridInfo: GradingInput;
  @Input("gridInfo")
  set gridInfo(val: GradingInput) {
    if (val.allowedValues && val.allowedValues.length > 0) {
      this.allowedValues = val.allowedValues.split(",");
      for (let i = 0; i < this.allowedValues.length; i++) {
        this.allowedValues[i] = parseInt(this.allowedValues[i]);
      }
    }

    this._gridInfo = val;
    switch (val.validationType) {
      case ValidationTypes.threeDigits:
        this.maxlength = 3;
        break;
      case ValidationTypes.threeDigitsDecimal:
        this.maxlength = 4;
        break;
      case ValidationTypes.threeDigitsDecimalMax10:
        this.maxlength = 4;
        this.threeDigitsDecimalMax10 = true;
        break;
      case ValidationTypes.threeDigitsDecimalMax10orTripleDigits:
        this.maxlength = 4;
        this.threeDigitsDecimalMax10orTripleDigits = true;
        break;
      default:
        this.maxlength = 1;
        break;
    }
    let tooltip;
    switch (val.etdrsGroup) {
      case "Dilated retinal vessels:":
        tooltip = `
        0: Absent
        1: Questionable
        2: Present, arterioles
        3: Present, venules
        4: Present, capillaries
        5: Present, cannot decide on type of vessel
        7: Cannot grade, obscuring lesions
        8: Cannot grade, photo quality
        9: Missing
      `;
        break;
      case "Loss retinal transparency (Presence of gray retinal tissue):":
      case "Visible telangiectatic vessels. Use CF and FFA (if available).":
      case "Blunted retinal vessels:":
      case "Right angle vein (correlate with FA):":
      case "Irregular RPE in macula:":
      case "Leakage on the RPE/outer retina":
      case "Leakage on the level of the inner capillary network":
        tooltip = `
        0: Absent
        1: Questionable
        2: Present
        7: Cannot grade, obscuring lesions
        8: Cannot grade, photo quality
        9: Missing
      `;
        break;
    }
    this.tooltip = tooltip;
  }

  private _eye: string;
  @Input("eye")
  set eye(val: string) {
    this._eye = val;
    this.setPositions(val);
    for (let i = 0; i < this._gridInfo.etdrsMappings.length; i++) {
      if (
        this._gridInfo.etdrsMappings[i].values &&
        this._gridInfo.etdrsMappings[i].values[this._eye]
      ) {
        this.inputModels[this._gridInfo.etdrsMappings[i].etdrsNumber - 1] =
          this._gridInfo.etdrsMappings[i].values[this._eye];
      }
    }
  }

  constructor() {
    this.inputModels = Array(9).fill(0);
  }

  private setPositions(eye: string) {
    this.inputs = [
      { position: 1 },
      { position: 3 },
      { position: 2 },
      { position: 5 },
      { position: 4 },
      { position: 7 },
      { position: 6 },
      { position: 9 },
      { position: 8 },
    ];
  }

  public nextInput(e: KeyboardEvent, input) {
    if (["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.key)) {
      if (input.position === 9) {
        // on final input, blur
        // incoming id is ="input.spreadsheetId + '-' + input.etdrsGroup + '-' + eye"
        const currentInputName = this.id + "-input-" + input.position;

        // const currentInputName = 'input-' + (input.position);
        const currentInput: NodeListOf<HTMLElement> =
          document.getElementsByName(currentInputName);
        if (currentInput && currentInput[0]) {
          (currentInput[0] as HTMLInputElement).blur();
        }
      } else {
        // find and go to next input
        const nextInputName = this.id + "-input-" + (input.position + 1);
        // const nextInputName = 'input-' + (input.position + 1);
        const nextInput: NodeListOf<HTMLElement> =
          document.getElementsByName(nextInputName);
        if (nextInput && nextInput[0]) {
          nextInput[0].focus();
          (nextInput[0] as HTMLInputElement).select();
        }
      }
    }
  }

  public populateAllFields(x: number) {
    for (let i = 0; i < this.inputModels.length; i++) {
      this.inputModels[i] = x;
    }
  }

  public checkGridValidity(): boolean {
    if (
      this.threeDigitsDecimalMax10 === true ||
      this.threeDigitsDecimalMax10orTripleDigits === true
    ) {
      for (let i = 0; i < this._gridInfo.etdrsMappings.length; i++) {
        const value =
          this.inputModels[this._gridInfo.etdrsMappings[i].etdrsNumber - 1];
        if (!this.threeDigitsDecimalMax10Validator(value)) {
          return false;
        }
      }
    }
    return true;
  }

  private threeDigitsDecimalMax10Validator(x): boolean {
    if (x === null || isNaN(x)) {
      return false; // invalid
    }
    if (x >= 0 && x < 10) {
      return true;
    } else if (
      this.threeDigitsDecimalMax10orTripleDigits &&
      ["777", "888", "999", 777, 888, 999].includes(x)
    ) {
      return true;
    } else {
      return false;
    }
  }

  public getValues(visit: string): GradingStagingData[] {
    const values: GradingStagingData[] = [];
    for (let i = 0; i < this._gridInfo.etdrsMappings.length; i++) {
      values.push({
        metaDataMappingId: this._gridInfo.etdrsMappings[i].metaDataMappingId,
        visit: visit,
        eye: this._eye,
        value:
          this.inputModels[this._gridInfo.etdrsMappings[i].etdrsNumber - 1] +
          "",
      });
    }
    return values;
  }

  public inputBlur(e) {
    // console.log('inputBlur:', e.target)
  }
}



// WEBPACK FOOTER //
// ./src/app/home/grading/etdrs-grid/etdrs-grid.component.ts